import { useEffect, useState } from "react";
import { Building2, Loader2, Plus, Pencil, Power, Globe, Users, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { TenantForm } from "../components/admin/TenantForm";
import { TenantFounders } from "../components/admin/TenantFounders";
import { TenantRMs } from "../components/admin/TenantRMs";

const fmt = (ts) => (ts ? new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const ORIGIN = API.replace(/\/api$/, "");

function TenantCard({ t, onEdit, onToggle, onFounders, onRMs }) {
  const th = t.thresholds || {};
  const active = t.status === "active";
  const dqCount = (th.disqualifiers?.length || 0) + (th.custom_disqualifiers?.length || 0);
  const docCount = (th.required_docs?.length || 0) + (th.custom_docs?.length || 0);
  return (
    <div className={"rounded-2xl border bg-neutral-950 p-4 " + (active ? "border-neutral-800" : "border-neutral-800/60 opacity-75")} data-testid={`tenant-card-${t.tenant_id}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-neutral-800" style={{ background: t.logo_url ? "#fff" : t.brand_primary_color }}>
          {t.logo_url ? <img src={`${ORIGIN}${t.logo_url}`} alt="" className="max-h-10 max-w-10 object-contain" /> : <Building2 className="h-5 w-5 text-white" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-sm font-bold text-white" data-testid={`tenant-name-${t.tenant_id}`}>{t.name}</h3>
            <span className={"rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide " + (active ? "bg-emerald-500/15 text-emerald-300" : "bg-neutral-700/60 text-neutral-300")} data-testid={`tenant-status-${t.tenant_id}`}>
              {active ? "Active" : "Inactive · offline"}
            </span>
          </div>
          <p className="mt-0.5 flex items-center gap-2 text-xs text-neutral-400"><Globe size={11} /> <span data-testid={`tenant-host-${t.tenant_id}`}>{t.hostname}</span>
            <a href={`/t/${t.subdomain}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[#7BD3CE] hover:underline" data-testid={`tenant-preview-${t.tenant_id}`}>preview portal <ExternalLink size={10} /></a></p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-neutral-400">
            <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-3 rounded-sm border border-neutral-700" style={{ background: t.brand_primary_color }} />{t.brand_primary_color}</span>
            <span>Min CRS <b className="text-neutral-200">{th.min_crs_score}</b></span>
            <span>Min DSCR <b className="text-neutral-200">{Number(th.min_dscr).toFixed(2)}x</b></span>
            <span><b className="text-neutral-200">{docCount}</b> required docs</span>
            <span><b className="text-neutral-200">{dqCount}</b> disqualifiers</span>
          </div>
          {(t.contact_name || t.contact_email) && <p className="mt-1.5 text-[11px] text-neutral-500">Contact: {[t.contact_name, t.contact_email].filter(Boolean).join(" · ")}</p>}
          <p className="mt-1 text-[10px] text-neutral-600">Created {fmt(t.created_at)} · Updated {fmt(t.updated_at)}{t.updated_by ? ` by ${t.updated_by}` : ""}</p>
        </div>
        <div className="flex shrink-0 gap-1.5">
          <button onClick={() => onFounders(t)} className="inline-flex items-center gap-1 rounded-lg border border-neutral-700 px-2.5 py-1.5 text-[11px] font-semibold text-neutral-200 hover:bg-neutral-800" data-testid={`tenant-founders-${t.tenant_id}`}><Users size={12} /> Founders</button>
          <button onClick={() => onRMs(t)} className="inline-flex items-center gap-1 rounded-lg border border-neutral-700 px-2.5 py-1.5 text-[11px] font-semibold text-neutral-200 hover:bg-neutral-800" data-testid={`tenant-rms-${t.tenant_id}`}><ShieldCheck size={12} /> RMs</button>
          <button onClick={() => onEdit(t)} className="inline-flex items-center gap-1 rounded-lg border border-neutral-700 px-2.5 py-1.5 text-[11px] font-semibold text-neutral-200 hover:bg-neutral-800" data-testid={`tenant-edit-${t.tenant_id}`}><Pencil size={12} /> Edit</button>
          <button onClick={() => onToggle(t)} className={"inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold " + (active ? "bg-red-500/10 text-red-300 hover:bg-red-500/20" : "bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20")} data-testid={`tenant-toggle-${t.tenant_id}`}>
            <Power size={12} /> {active ? "Deactivate" : "Reactivate"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminTenants() {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null); // null | "new" | tenant object

  const load = () => api.get("/admin/tenants").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load tenants"));
  useEffect(() => { load(); }, []);

  const upsert = (t) => setData((d) => ({ ...d, tenants: d.tenants.some((x) => x.tenant_id === t.tenant_id) ? d.tenants.map((x) => (x.tenant_id === t.tenant_id ? t : x)) : [t, ...d.tenants] }));

  const toggle = async (t) => {
    const active = t.status === "active";
    if (active && !window.confirm(`Deactivate ${t.name}? ${t.hostname} goes offline immediately.`)) return;
    try {
      const { data: upd } = await api.post(`/admin/tenants/${t.tenant_id}/${active ? "deactivate" : "activate"}`);
      upsert(upd); toast.success(active ? `${t.name} deactivated — ${t.hostname} is offline` : `${t.name} is live again`);
    } catch { toast.error("Couldn't update tenant status"); }
  };

  const tenants = data?.tenants || [];
  return (
    <AdminShell current="/admin/tenants">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3" data-testid="admin-tenants-page">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0A8B85]/15 text-[#7BD3CE]"><Building2 className="h-5 w-5" /></span>
          <div>
            <h1 className="text-xl font-extrabold text-white">Tenants</h1>
            <p className="text-sm text-neutral-400">Bank and institution white-label tenants: branding, subdomain and qualification thresholds.</p>
          </div>
        </div>
        {mode === null && (
          <button onClick={() => setMode("new")} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0A8B85] px-3 py-2 text-xs font-bold text-white hover:bg-[#087a74]" data-testid="tenant-new-btn"><Plus size={14} /> New tenant</button>
        )}
      </div>

      {!data ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]" /></div>
      ) : mode?.view === "founders" ? (
        <TenantFounders tenant={mode.tenant} onBack={() => setMode(null)} />
      ) : mode?.view === "rms" ? (
        <TenantRMs tenant={mode.tenant} onBack={() => setMode(null)} />
      ) : mode !== null ? (
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-5">
          <h2 className="mb-4 text-base font-bold text-white" data-testid="tenant-form-title">{mode === "new" ? "New tenant" : `Edit ${mode.name}`}</h2>
          <TenantForm key={mode === "new" ? "new" : mode.tenant_id} tenant={mode === "new" ? null : mode} presets={data.presets}
            onCancel={() => setMode(null)}
            onLogoChanged={(t) => { upsert(t); setMode(t); }}
            onSaved={(t, created) => { upsert(t); setMode(created ? t : null); }} />
        </div>
      ) : tenants.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-800 p-12 text-center text-sm text-neutral-500" data-testid="tenants-empty">No tenants yet. Create the first one to get started.</div>
      ) : (
        <div className="space-y-3" data-testid="tenants-list">
          {tenants.map((t) => <TenantCard key={t.tenant_id} t={t} onEdit={setMode} onToggle={toggle} onFounders={(x) => setMode({ view: "founders", tenant: x })} onRMs={(x) => setMode({ view: "rms", tenant: x })} />)}
        </div>
      )}
    </AdminShell>
  );
}
